/**
 * A table cell that can carry a comment thread (OR-1534, Figma "Notes").
 *
 * - Hovering the cell reveals the comment icon at its right edge. It sits
 *   absolutely in the cell's right padding (the `<td>` is `relative`, `px-6`),
 *   so it neither covers the content — the Figma's explicit ask — nor takes
 *   width from it (reserving a slot truncated every column noticeably).
 * - After ~1 s of hover the icon's tooltip reads "Comment", or "Open
 *   discussion" once the cell has open comments.
 * - A cell with open comments is marked like the industry norm the Figma
 *   cites (Notion): a pale yellow fill with a yellow rule underneath, with a
 *   little room around badges and dropdowns. On hover the icon shows the count.
 * - Clicking opens the thread popover below the cell. It closes on any
 *   outside click (no X while empty, as in Notion).
 *
 * Hovering anywhere in the host `<td>`, padding included, reveals the icon.
 * That's a plain `td:hover` selector rather than `group-hover`: the
 * react-aria Tailwind plugin rewrites `group-hover` on RAC elements to
 * `[data-hovered]`, which a table cell never gets.
 */
import { useRef } from "react";
import type { ReactNode, SyntheticEvent } from "react";
import { MessageTextSquare01 } from "@untitledui/icons";
import {
    Button as AriaButton,
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    Popover as AriaPopover,
} from "react-aria-components";
import { Tooltip, cx } from "@tricura/atlas-ui";
import type { CommentPage, SubmissionCommentThreadWire } from "../../types/submission-comment";
import { CommentThreadPanel } from "./comment-thread-panel";

/** The Figma: "We can show the tooltip when they hover for a second". */
const TOOLTIP_DELAY_MS = 1000;

/** The popover is a React child of the table row even though it renders in a
 *  portal, so its events bubble up the React tree into the row — a click in
 *  the textarea would navigate to the submission and Enter / arrow keys
 *  would drive the grid. Stop them at the anchor. */
const stop = (e: SyntheticEvent) => e.stopPropagation();
const STOP_EVENTS = {
    onClick: stop,
    onPointerDown: stop,
    onPointerUp: stop,
    onMouseDown: stop,
    onKeyDown: stop,
    onKeyUp: stop,
};

export interface CommentCellProps {
    submissionId: string;
    page: CommentPage;
    field: string;
    /** Open comments on this cell (0 = none). */
    count: number;
    /** Badge / dropdown cell: the yellow fill gets room around the control. */
    isControl?: boolean;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onCountChange: (count: number) => void;
    onResolved: (thread: SubmissionCommentThreadWire) => void;
    children: ReactNode;
}

export function CommentCell({
    submissionId,
    page,
    field,
    count,
    isControl,
    isOpen,
    onOpenChange,
    onCountChange,
    onResolved,
    children,
}: CommentCellProps) {
    const anchorRef = useRef<HTMLDivElement>(null);
    const hasComments = count > 0;
    const label = hasComments ? "Open discussion" : "Comment";

    return (
        <div ref={anchorRef} className="w-full min-w-0" data-autotest="comment-cell" data-comment-count={count}>
            {hasComments ? (
                <span
                    data-autotest="comment-cell-highlight"
                    // Figma commented state: utility-yellow-50 fill (#FEFCE8) with a
                    // 1px fg-warning-secondary (#EAB308) rule underneath. Text hugs it (21px); a badge or
                    // dropdown sits inside ~4px of fill so the yellow shows around the
                    // control (Figma node 112-23397). The negative margin keeps that
                    // padding (and the 1px rule) from moving the control or the row height.
                    className={cx(
                        "bg-utility-yellow-50 border-fg-warning-secondary inline-block max-w-full border-b align-middle",
                        isControl && "-mx-1 -mt-1 -mb-[5px] max-w-[calc(100%+0.5rem)] p-1",
                    )}
                >
                    {children}
                </span>
            ) : (
                children
            )}

            <span className="absolute top-1/2 right-0.5 -translate-y-1/2" {...STOP_EVENTS}>
                <AriaDialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
                    <Tooltip title={label} delay={TOOLTIP_DELAY_MS} placement="top">
                        <AriaButton
                            aria-label={label}
                            data-autotest="comment-cell-trigger"
                            className={cx(
                                "text-fg-quaternary hover:text-fg-secondary border-secondary bg-primary hover:bg-secondary_hover inline-flex h-5 min-w-5 cursor-pointer items-center justify-center gap-0.5 rounded-md border px-0.5 text-[11px] font-medium tabular-nums outline-none transition duration-100 ease-linear focus-visible:ring-2 focus-visible:ring-focus-ring",
                                // Hidden until the cell is hovered / focused; stays up while open.
                                isOpen ? "ring-focus-ring opacity-100 ring-2" : "opacity-0 focus-visible:opacity-100 [td:hover_&]:opacity-100",
                            )}
                        >
                            <MessageTextSquare01 aria-hidden="true" className="size-3" />
                            {hasComments && <span>{count}</span>}
                        </AriaButton>
                    </Tooltip>
                    <AriaPopover
                        triggerRef={anchorRef}
                        placement="bottom start"
                        offset={6}
                        // react-aria caps the popover's `max-height` to the room left in
                        // the viewport (a cell low on the page gets little). Flex column
                        // + `min-h-0` down the chain lets the panel shrink into that cap
                        // so its comment list scrolls, instead of overflowing off-screen.
                        className="z-50 flex flex-col entering:animate-in entering:fade-in entering:zoom-in-95"
                    >
                        <AriaDialog
                            aria-label="Comments"
                            data-autotest="comment-popover"
                            className="bg-primary border-secondary flex min-h-0 w-[400px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border shadow-lg outline-none"
                        >
                            <CommentThreadPanel
                                submissionId={submissionId}
                                page={page}
                                field={field}
                                knownCount={count}
                                onCountChange={onCountChange}
                                onResolved={onResolved}
                                onClose={() => onOpenChange(false)}
                            />
                        </AriaDialog>
                    </AriaPopover>
                </AriaDialogTrigger>
            </span>
        </div>
    );
}
